import * as React from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, CalendarPlus, CheckCircle2, ChevronDown, Clock, FileWarning, MessageSquare, Plus, Search, Truck } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Gaveta } from '@/componentes/ui/gaveta';
import { Campo } from '@/componentes/ui/primitivos';
import { Seletor } from '@/componentes/ui/seletor';
import { moeda, textoJaTransferida, type DetalheNota, type Destino, type Doh, type ValorOc } from './tipos';

/**
 * Agendar: onde, quando e quem traz. Empresa, centro, série e se precisa de
 * transferência saem do cadastro do destino.
 *
 * Refeita em 27/10/2026 (Paulo: "campos mal distribuídos"): data com
 * calendário e atalhos, janela com horário no padrão HH:MM às HH:MM,
 * transportadora buscada no cadastro (migration 026) e o DOH num quadro que
 * diz numa frase se trava e por quê, com o detalhe por item ao abrir.
 */
export function GavetaAgendar({ chaves, aoFechar }: { chaves: string[]; aoFechar: () => void }): JSX.Element {
  const { temPermissao, usuario } = useSessao();
  const login = usuario?.nome || usuario?.login;
  const cliente = useQueryClient();
  const [destinoId, definirDestino] = React.useState('');
  const [data, definirData] = React.useState(isoDaqui(1));
  const [janela, definirJanela] = React.useState('08:00-12:00');
  const [modalidade, definirModalidade] = React.useState<'ENTREGA' | 'COLETA'>('ENTREGA');
  const [transportadora, definirTransportadora] = React.useState('');
  const [motivoDoh, definirMotivoDoh] = React.useState('');

  const destinos = useQuery<{ destinos: Destino[] }>({
    queryKey: ['entradas-destinos'],
    queryFn: () => api.get('/api/entradas/destinos'),
    enabled: chaves.length > 0,
  });
  const dohs = useQueries({
    // O DOH é o do DESTINO (estoque e venda dos centros dele): só calcula com
    // o destino escolhido, e recalcula quando ele muda.
    queries: chaves.map((c) => ({
      queryKey: ['entrada-doh', c, destinoId],
      queryFn: () => api.get<Doh>(`/api/entradas/nota/${c}/doh?destino=${destinoId}`),
      enabled: Boolean(destinoId),
    })),
  });
  // As notas (mesmo cache da gaveta da nota): empresa e pré-destino.
  const notas = useQueries({
    queries: chaves.map((c) => ({ queryKey: ['entrada', c], queryFn: () => api.get<DetalheNota>(`/api/entradas/nota/${c}`) })),
  })
    .map((q) => q.data?.nota)
    .filter((n): n is NonNullable<typeof n> => Boolean(n));
  // Todas as notas ficam no armazém da própria empresa: já vem escolhido (a logística só confirma).
  const sugeridos = [...new Set(notas.map((n) => n.preDestino?.destinoId ?? null))];
  const sugerido = notas.length === chaves.length && sugeridos.length === 1 ? sugeridos[0] : null;
  const jaSugeriu = React.useRef(false);
  React.useEffect(() => {
    if (sugerido && !destinoId && !jaSugeriu.current) {
      jaSugeriu.current = true;
      definirDestino(String(sugerido));
    }
  }, [sugerido, destinoId]);
  const empresasDasNotas = [...new Set(notas.map((n) => n.empresa))];
  const travadas = dohs.filter((q) => q.data?.travado);
  const podeLiberar = temPermissao('entradas.liberar_doh');
  // Com o parâmetro ligado, acima do DOH ninguém libera aqui: vira pedido no Slack.
  const exigeSlack = dohs.some((q) => q.data?.exigeAprovacaoSlack);
  const pendente = dohs.map((q) => q.data?.aprovacaoPendente).find(Boolean) ?? null;
  // Acima do DOH: pedir aprovação no Slack é para todos. Quem tem a permissão
  // pode liberar direto (se o parâmetro não obriga o Slack) ou pedir também.
  const [querLiberar, definirQuerLiberar] = React.useState(true);
  const modoSlack = exigeSlack || !podeLiberar || !querLiberar;
  const [anexarDanfe, definirAnexarDanfe] = React.useState(true);
  const [enviado, definirEnviado] = React.useState<{ id: number; comoPessoa: boolean; link: string | null; avisoAnexo: string | null } | null>(null);

  const agendar = useMutation({
    mutationFn: () =>
      api.post<{
        agendamento: number | null;
        agendadas: string[];
        travadas: unknown[];
        aprovacao?: number;
        comoPessoa?: boolean;
        link?: string | null;
        anexos?: { ok: boolean; erro?: string }[];
      }>('/api/entradas/agendar', {
        chaves,
        destinoId: Number(destinoId),
        data,
        janela: janela || null,
        modalidade,
        transportadora: transportadora || null,
        liberarDoh: travadas.length && !modoSlack ? { motivo: motivoDoh } : null,
        pedirAprovacao: travadas.length && modoSlack ? { motivo: motivoDoh, anexarDanfe } : null,
        divergenciaAprovada: divergencias.length && aprovadoPor.trim() ? aprovadoPor.trim() : null,
      }),
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
      void cliente.invalidateQueries({ queryKey: ['entradas-transportadoras'] });
      for (const c of chaves) void cliente.invalidateQueries({ queryKey: ['entrada', c] });
      if (r.agendamento) aoFechar();
      if (r.aprovacao) {
        const falhou = (r.anexos ?? []).find((a) => !a.ok);
        definirEnviado({
          id: r.aprovacao,
          comoPessoa: r.comoPessoa !== false,
          link: r.link ?? null,
          avisoAnexo: falhou ? `A DANFE não foi anexada: ${falhou.erro ?? 'erro'}` : null,
        });
        for (const c of chaves) void cliente.invalidateQueries({ queryKey: ['entrada-doh', c] });
      }
    },
  });

  const ativos = (destinos.data?.destinos ?? []).filter((d) => d.ativo);
  const escolhido = ativos.find((d) => String(d.id) === destinoId);
  const erro = agendar.error instanceof ErroHttp ? agendar.error.message : null;
  const janelaOk = janela === '' || janelaValida(janela);
  const bloqueado = travadas.length > 0 && !motivoDoh.trim();
  const jaTransferidas = notas.filter((n) => n.jaTransferida);
  // Importada sem validação: agenda, mas a NF de transferência só sai depois que o fiscal salvar no Procfit.
  const semValidacao = notas.filter((n) => n.compraSemValidacao);
  const semOc = notas.filter((n) => n.pedidoCompra == null);
  // Divergência não trava: quem agenda vê o que diverge e escolhe seguir (fica na linha do
  // tempo) ou pedir antes ao comprador no Slack (pedido do Paulo, 30/10/2026).
  const ocsDasNotas = [...new Set(notas.map((n) => n.pedidoCompra).filter((oc): oc is number => oc != null))].sort();
  const valoresOcs = useQuery<{ ocs: Record<number, ValorOc> }>({
    queryKey: ['entradas-ocs', ocsDasNotas.join(',')],
    queryFn: () => api.get('/api/entradas/ocs', { ocs: ocsDasNotas.join(',') }),
    enabled: ocsDasNotas.length > 0,
  });
  const divergencias = notas
    .map((n) => {
      // Já aprovada (a nota ou a OC): não cobra de novo — o mesmo critério da trava no servidor.
      const v0 = n.pedidoCompra != null ? valoresOcs.data?.ocs[n.pedidoCompra] : undefined;
      const v = v0?.autorizada || n.aprovacao ? undefined : v0;
      const pre = v?.prePedido?.itens.length ? v.prePedido : null;
      const partes = [
        pre
          ? `OC ${n.pedidoCompra} difere do pré-pedido ${pre.numero} em ${pre.itens.length} ${pre.itens.length === 1 ? 'item' : 'itens'} (${difTexto(pre.itens.reduce((s, i) => s + (i.valorOc ?? 0) - (i.valorPre ?? 0), 0))})`
          : null,
        v?.acima ? `NFs da OC somam ${moeda(v.valorNotas)}, acima da OC (${moeda(v.valorOc)})` : null,
        n.divergenciasAbertas > 0 ? `${n.divergenciasAbertas} divergência(s) da nota com a OC em aberto` : null,
      ].filter((x): x is string => Boolean(x));
      return partes.length ? { nota: n, partes } : null;
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x));
  // Regras do Cadastro: divergência pode exigir aprovação; sem OC pode travar.
  const regras = useQuery<{ exigeAprovacaoDivergencia: boolean; bloqueiaSemOc: boolean }>({
    queryKey: ['entradas-regras-agendamento'],
    queryFn: () => api.get('/api/entradas/regras-agendamento'),
  });
  const exigeAprovacao = Boolean(regras.data?.exigeAprovacaoDivergencia);
  const travaSemOc = Boolean(regras.data?.bloqueiaSemOc);
  const [escolhaDivLivre, definirEscolhaDiv] = React.useState<'seguir' | 'slack' | null>(null);
  const escolhaDiv = exigeAprovacao ? 'slack' : escolhaDivLivre;
  const [aprovadoPor, definirAprovadoPor] = React.useState('');
  const [pedidoDiv, definirPedidoDiv] = React.useState<{ id: number; link: string | null } | null>(null);
  // Quem marcar no pedido: começa com quem aprova DOH; dá para tirar e pôr gente do canal.
  const [marcados, definirMarcados] = React.useState<string[] | null>(null);
  const [buscaPessoa, definirBuscaPessoa] = React.useState('');
  const aprovadores = useQuery<{ pessoas: { id: string; nome: string | null }[] }>({
    queryKey: ['entradas-aprovadores-doh'],
    queryFn: () => api.get('/api/entradas/aprovadores-doh'),
    enabled: escolhaDiv === 'slack',
  });
  const membrosCanal = useQuery<{ canal: string | null; pessoas: { id: string; nome: string; titulo: string | null }[] }>({
    queryKey: ['entrada-slack-membros', chaves[0]],
    queryFn: () => api.get(`/api/entradas/nota/${chaves[0]}/slack-membros`),
    enabled: escolhaDiv === 'slack' && Boolean(chaves[0]),
    staleTime: 30_000,
  });
  React.useEffect(() => {
    if (marcados == null && aprovadores.data) definirMarcados(aprovadores.data.pessoas.map((x) => x.id));
  }, [marcados, aprovadores.data]);
  const nomeDe = (id: string): string =>
    membrosCanal.data?.pessoas.find((x) => x.id === id)?.nome ?? aprovadores.data?.pessoas.find((x) => x.id === id)?.nome ?? id;
  const ehAprovador = (id: string): boolean => Boolean(aprovadores.data?.pessoas.some((x) => x.id === id));
  const listaMarcados = marcados ?? [];
  const termoPessoa = buscaPessoa.trim().toLowerCase();
  const sugestoes = termoPessoa
    ? (membrosCanal.data?.pessoas ?? [])
        .filter((x) => !listaMarcados.includes(x.id))
        .filter((x) => x.nome.toLowerCase().includes(termoPessoa) || (x.titulo ?? '').toLowerCase().includes(termoPessoa))
        .slice(0, 6)
    : [];
  const pedirDiv = useMutation({
    mutationFn: () =>
      // O pedido leva tudo o que foi preenchido: aprovado no Slack, o WMS agenda com estes dados.
      api.post<{ id: number; ocs: number[]; link: string | null }>('/api/entradas/agendar/pedir-aprovacao-divergencia', {
        chaves,
        destinoId: Number(destinoId),
        data,
        janela: janela || null,
        modalidade,
        transportadora: transportadora || null,
        marcar: listaMarcados,
      }),
    onSuccess: (r) => {
      definirPedidoDiv({ id: r.id, link: r.link });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
      for (const c of chaves) void cliente.invalidateQueries({ queryKey: ['entrada', c] });
    },
  });
  const falta = jaTransferidas.length
    ? 'Já transferida no Procfit'
    : travaSemOc && semOc.length
      ? 'Sem OC não agenda: indique a OC'
    : !destinoId
      ? 'Escolha o destino'
      : !data
        ? 'Escolha a data'
        : !janelaOk
          ? 'Corrija a janela'
          : pendente || enviado
            ? 'Aguardando aprovação no Slack'
            : divergencias.length && !escolhaDiv
              ? 'Escolha: seguir ou pedir aprovação'
              : pedidoDiv && escolhaDiv === 'slack'
                ? 'Aprovação pedida no Slack'
                : escolhaDiv === 'slack' && divergencias.length && !listaMarcados.length
                  ? 'Escolha quem marcar no Slack'
            : bloqueado
              ? modoSlack
                ? 'Diga o motivo para pedir aprovação'
                : 'Diga o motivo para liberar'
              : null;
  const pedeAprovacao = travadas.length > 0 && modoSlack;
  const pedeDivSlack = divergencias.length > 0 && escolhaDiv === 'slack';

  return (
    <Gaveta
      aberta={chaves.length > 0}
      aoFechar={aoFechar}
      titulo="Agendar recebimento"
      subtitulo={`${chaves.length} nota(s) no mesmo agendamento`}
      className="sm:w-[min(100vw,max(480px,45vw))]"
    >
      <div className="space-y-5 p-5">
        {/* Sem OC não trava, mas quem agenda fica sabendo que está fora do fluxo e que fica registrado. */}
        {semOc.length ? (
          <div className="flex gap-2.5 rounded-lg border border-status-atencao/50 bg-status-atencaoFundo px-3 py-2.5 text-xs text-status-atencao">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1">
              <p className="font-semibold">
                {semOc.length === chaves.length ? 'Agendamento fora do fluxo: ' : `${semOc.length} de ${chaves.length} notas fora do fluxo: `}
                {semOc.map((n) => `NF ${n.numero}`).join(', ')} sem OC.
              </p>
              {travaSemOc ? (
                <p>
                  <b>Não agenda sem OC</b> (regra do Cadastro). Peça ao comprador a OC destas notas: indicada a OC, elas são conferidas e o
                  agendamento libera.
                </p>
              ) : (
                <p>
                  Você pode agendar, mas a linha do tempo da nota vai registrar que <b>{login ?? 'você'}</b> agendou sem OC. Peça ao
                  comprador que valide a OC antes da chegada; sem ela a cega não confere quantidade nem preço.
                </p>
              )}
            </div>
          </div>
        ) : null}
        {divergencias.length ? (
          <div className="space-y-2.5 rounded-lg border border-status-divergencia/40 bg-status-divergenciaFundo px-3 py-2.5 text-xs">
            <div className="flex gap-2.5 text-status-divergencia">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold">
                  Agendamento com divergência: {divergencias.length === chaves.length ? 'todas as notas' : `${divergencias.length} de ${chaves.length} notas`}
                </p>
                <ul className="space-y-0.5 text-texto">
                  {divergencias.map((d) => (
                    <li key={d.nota.chave}>
                      <b>NF {d.nota.numero}</b>: {d.partes.join(' · ')}
                    </li>
                  ))}
                </ul>
                <p className="text-textoFraco">
                  {exigeAprovacao
                    ? 'Pela regra do Cadastro, nota com divergência só agenda com aprovação no Slack. Tudo fica na linha do tempo das notas.'
                    : 'Não trava. O que você escolher fica registrado na linha do tempo das notas, com o seu nome.'}
                </p>
              </div>
            </div>
            <div className={cn('grid gap-2', !exigeAprovacao && 'sm:grid-cols-2')}>
              {(
                [
                  ['seguir', CalendarPlus, 'Seguir com o agendamento', 'Agenda agora; a linha do tempo registra que você agendou com a divergência.'],
                  ['slack', MessageSquare, 'Pedir aprovação no Slack', 'Marca quem aprova DOH com o que diverge; aprovado no Slack, o WMS agenda sozinho.'],
                ] as const
              )
                // Com a regra de aprovação ligada, "Seguir" não existe.
                .filter(([v]) => !exigeAprovacao || v === 'slack')
                .map(([v, Icone, titulo, ajuda]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={escolhaDiv === v}
                  onClick={() => definirEscolhaDiv(v)}
                  className={cn(
                    'flex items-start gap-2 rounded-lg border bg-superficie px-3 py-2 text-left transition',
                    escolhaDiv === v ? 'border-primaria ring-2 ring-primaria/30' : 'border-borda hover:border-bordaForte',
                  )}
                >
                  <Icone className={cn('mt-0.5 h-4 w-4 shrink-0', escolhaDiv === v ? 'text-primaria' : 'text-textoFraco')} />
                  <span>
                    <b className="block text-texto">{titulo}</b>
                    <span className="text-textoFraco">{ajuda}</span>
                  </span>
                </button>
              ))}
            </div>
            {pedidoDiv ? (
              <p className="rounded-md bg-superficie px-3 py-2 text-texto">
                Pedido {pedidoDiv.id} enviado em seu nome no canal de compras. Quem foi marcado reage ✅ ou responde <b>aprovar</b> na
                thread, e o WMS agenda sozinho com estes dados; <b>cancelar</b> não agenda; <b>aguardar</b> deixa em aberto. Tudo fica na linha do tempo.
                {pedidoDiv.link ? (
                  <>
                    {' '}
                    <a href={pedidoDiv.link} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                      Abrir no Slack
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}
            {escolhaDiv === 'slack' && !pedidoDiv ? (
              <div className="space-y-1">
                <span className="font-semibold text-texto">Marcar no Slack</span>
                <div className="rounded-lg border border-borda bg-superficie">
                  <div className="flex min-h-9 flex-wrap items-center gap-1.5 px-2 py-1.5">
                    {listaMarcados.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[#e8f5fa] py-0.5 pl-2 pr-1 font-semibold text-[#1264a3]">
                        @{nomeDe(id)}
                        {ehAprovador(id) ? <span className="font-normal text-[#1264a3]/70">· aprova DOH</span> : null}
                        <button
                          type="button"
                          aria-label={`Tirar ${nomeDe(id)}`}
                          className="rounded-full px-1 hover:bg-[#d1ebf5]"
                          onClick={() => definirMarcados(listaMarcados.filter((x) => x !== id))}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      className="min-w-[9rem] flex-1 border-0 bg-transparent p-0 text-xs shadow-none outline-none ring-0 placeholder:text-textoSuave focus:border-0 focus:outline-none focus:ring-0"
                      placeholder={listaMarcados.length ? 'Marcar mais alguém…' : 'Buscar pessoa do canal…'}
                      value={buscaPessoa}
                      onChange={(e) => definirBuscaPessoa(e.target.value)}
                    />
                  </div>
                  {sugestoes.length ? (
                    <ul className="max-h-48 divide-y divide-borda overflow-y-auto border-t border-borda">
                      {sugestoes.map((x) => (
                        <li key={x.id}>
                          <button
                            type="button"
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-superficie2"
                            onClick={() => {
                              definirMarcados([...listaMarcados, x.id]);
                              definirBuscaPessoa('');
                            }}
                          >
                            <span className="truncate font-semibold text-texto">{x.nome}</span>
                            <span className="truncate text-textoSuave">{x.titulo ?? ''}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : termoPessoa && membrosCanal.data ? (
                    <p className="border-t border-borda px-3 py-1.5 text-textoSuave">Ninguém do canal com esse nome.</p>
                  ) : null}
                </div>
                <p className="text-textoSuave">
                  Já vem com quem aprova DOH. Quem for marcado fica registrado na linha do tempo das notas.
                </p>
              </div>
            ) : null}
            {escolhaDiv === 'seguir' ? (
              <label className="block space-y-1">
                <span className="font-semibold text-texto">Já aprovado por alguém? (opcional)</span>
                <Campo placeholder="Ex.: Robson aprovou na thread do Slack às 14h" value={aprovadoPor} onChange={(e) => definirAprovadoPor(e.target.value)} />
              </label>
            ) : null}
            {pedirDiv.error ? (
              <p className="text-status-divergencia">{pedirDiv.error instanceof ErroHttp ? pedirDiv.error.message : String(pedirDiv.error)}</p>
            ) : null}
          </div>
        ) : null}
        {/* -------- onde -------- */}
        <Bloco titulo="Destino">
          <Seletor
            rotuloAcessivel="Destino"
            vazio="Escolha para onde a carga vai"
            valor={destinoId}
            aoMudar={definirDestino}
            className="w-full"
            opcoes={ativos.map((d) => ({
              valor: String(d.id),
              rotulo: `${d.nome} ${d.uf}`,
              auxiliar: d.tipo === 'FULL' ? 'Full · sem cega' : 'Armazém · com cega',
            }))}
          />
          {escolhido ? (
            <p className="text-xs text-textoFraco">
              {escolhido.tipo === 'FULL'
                ? `Empresa ${escolhido.empresa}: a nota segue para "A transferir" e, vinculada a transferência, para o trânsito do Full.`
                : empresasDasNotas.length === 1 && escolhido.empresa === empresasDasNotas[0]
                  ? 'Mesma empresa da nota de compra: fica aqui, sem transferência; na chegada, a cega sai direto.'
                  : `Outro CNPJ (empresa ${escolhido.empresa}): a nota espera a NF de transferência antes da chegada.`}
              {escolhido.observacao ? ` ${escolhido.observacao}` : ''}
            </p>
          ) : null}
          {semValidacao.length && escolhido && !(empresasDasNotas.length === 1 && escolhido.empresa === empresasDasNotas[0]) ? (
            <div className="flex gap-3 rounded-lg border border-borda border-l-4 border-l-status-atencao bg-superficie px-3 py-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-status-atencaoFundo text-status-atencao">
                <FileWarning className="h-4 w-4" />
              </span>
              <div className="min-w-0 space-y-0.5 text-xs">
                <p className="text-[13px] font-semibold text-texto">
                  {semValidacao.length === 1
                    ? `A compra da NF ${semValidacao[0]!.numero} não foi validada no ERP`
                    : `${semValidacao.length} compras não foram validadas no ERP`}
                </p>
                <p className="text-textoFraco">
                  NF de compra {semValidacao.map((n) => n.nfCompra).join(', ')}: importada sem validação, ainda sem estoque.
                </p>
                <p className="font-medium text-status-atencao">
                  Pode agendar, mas a NF de transferência só sai depois que o fiscal validar a compra (botão “Validar no ERP” no roteiro da nota).
                </p>
              </div>
            </div>
          ) : null}
          {jaTransferidas.map((n) => {
            const j = n.jaTransferida!;
            return (
              // Cartão com a borda da cor do alerta, em vez de uma frase inteira em vermelho negrito.
              <div
                key={n.chave}
                title={textoJaTransferida(j)}
                className="flex gap-3 rounded-lg border border-borda border-l-4 border-l-status-divergencia bg-superficie px-3 py-2.5"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-status-divergenciaFundo text-status-divergencia">
                  <Truck className="h-4 w-4" />
                </span>
                <div className="min-w-0 space-y-0.5 text-xs">
                  <p className="text-[13px] font-semibold text-texto">NF {n.numero} já foi transferida no Procfit</p>
                  <p className="text-textoFraco">
                    Transferência <b className="font-medium text-texto">NF {j.nf}</b> em {j.data.split('-').reverse().join('/')}
                    {j.centroDestino
                      ? ` · entrou na empresa ${j.empresaDestino ?? '?'}, centro ${j.centroDestino}`
                      : ' · ainda sem entrada no destino'}
                  </p>
                  <p className="font-medium text-status-divergencia">Não agende: a mercadoria já saiu.</p>
                </div>
              </div>
            );
          })}
          {notas.some((n) => n.preDestino) ? (
            <p className="text-xs text-textoFraco">
              Pela entrada fiscal:{' '}
              {[...new Set(notas.map((n) => n.preDestino?.texto).filter(Boolean))].join(' · ')}
              {sugerido && String(sugerido) === destinoId ? ' (já escolhido acima)' : ''}
            </p>
          ) : null}
        </Bloco>

        {/* -------- quando -------- */}
        <div className="grid gap-5 lg:grid-cols-2">
          <Bloco titulo="Data" icone={<CalendarDays className="h-3.5 w-3.5" />}>
            <CampoData valor={data} aoMudar={definirData} />
          </Bloco>
          <Bloco titulo="Janela de entrega" icone={<Clock className="h-3.5 w-3.5" />}>
            <CampoJanela valor={janela} aoMudar={definirJanela} />
          </Bloco>
        </div>

        {/* -------- quem traz -------- */}
        <div className="grid gap-5 lg:grid-cols-[auto_1fr]">
          <Bloco titulo="Modalidade">
            <div className="inline-flex rounded-lg bg-superficie2 p-0.5 ring-1 ring-inset ring-borda">
              {(['ENTREGA', 'COLETA'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => definirModalidade(m)}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-xs font-semibold transition',
                    modalidade === m ? 'bg-superficie text-texto shadow-xs' : 'text-textoFraco hover:text-texto',
                  )}
                >
                  {m === 'ENTREGA' ? 'Fornecedor entrega' : 'Nós coletamos'}
                </button>
              ))}
            </div>
          </Bloco>
          <Bloco titulo="Transportadora" icone={<Truck className="h-3.5 w-3.5" />}>
            <CampoTransportadora valor={transportadora} aoMudar={definirTransportadora} />
          </Bloco>
        </div>

        {/* -------- DOH -------- */}
        {destinoId ? (
          <QuadroDoh chaves={chaves} dohs={dohs.map((q) => q.data)} />
        ) : (
          <p className="rounded-xl border border-borda px-4 py-3 text-xs text-textoFraco">
            Escolha o destino para calcular o DOH: ele é medido no estoque e na venda do destino (ex.: Meli Full MG = 1501), não no grupo todo.
          </p>
        )}
        {pendente || enviado ? (
          <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">
            {enviado ? (
              <>
                Pedido {enviado.id} enviado ao Slack{enviado.comoPessoa ? ' em seu nome' : ' pelo bot do WMS (conecte seu Slack para sair em seu nome)'}, marcando quem
                aprova DOH. Eles respondem na thread <b>aprovar</b>, <b>cancelar</b> ou <b>aguardar</b>; aprovado, o WMS agenda sozinho com estes dados.
                {enviado.link ? (
                  <>
                    {' '}
                    <a href={enviado.link} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                      Abrir no Slack
                    </a>
                  </>
                ) : null}
                {enviado.avisoAnexo ? <span className="mt-1 block">{enviado.avisoAnexo}</span> : null}
              </>
            ) : (
              `Aguardando aprovação no Slack desde ${new Date(pendente!.quando).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} (pedido ${pendente!.id}, de ${pendente!.solicitante}).`
            )}
          </p>
        ) : travadas.length ? (
          <Bloco titulo={modoSlack ? 'Pedir aprovação no Slack' : 'Liberar mesmo assim'}>
            {!exigeSlack && podeLiberar ? (
              <div className="inline-flex rounded-lg bg-superficie2 p-0.5 ring-1 ring-inset ring-borda">
                {(
                  [
                    [true, 'Liberar eu mesmo'],
                    [false, 'Pedir aprovação no Slack'],
                  ] as const
                ).map(([v, rotulo]) => (
                  <button
                    key={rotulo}
                    type="button"
                    onClick={() => definirQuerLiberar(v)}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-semibold transition',
                      querLiberar === v ? 'bg-superficie text-texto shadow-xs' : 'text-textoFraco hover:text-texto',
                    )}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            ) : null}
            <Campo
              placeholder={
                modoSlack ? 'Por que agendar mesmo assim? (vai para quem aprova, com os itens)' : 'Por que agendar mesmo assim? (vai para o histórico da nota)'
              }
              value={motivoDoh}
              onChange={(e) => definirMotivoDoh(e.target.value)}
            />
            {modoSlack ? (
              <>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[rgb(var(--cor-primaria))]"
                    checked={anexarDanfe}
                    onChange={(e) => definirAnexarDanfe(e.target.checked)}
                  />
                  Anexar a DANFE na thread
                </label>
                <p className="text-xs text-textoFraco">
                  Sai em seu nome no canal de compras, com a nota, a chave e os itens acima do DOH ou sem histórico, marcando quem aprova. Eles respondem na thread{' '}
                  <b>aprovar</b>, <b>cancelar</b> ou <b>aguardar</b>, e o WMS segue sozinho.
                </p>
              </>
            ) : null}
          </Bloco>
        ) : null}

        {erro ? <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">{erro}</p> : null}
        <div className="sticky bottom-0 -mx-5 border-t border-borda bg-superficie px-5 py-3">
          <Botao
            variante="primaria"
            className="w-full"
            disabled={Boolean(falta)}
            carregando={agendar.isPending || pedirDiv.isPending}
            onClick={() => (pedeDivSlack ? pedirDiv.mutate() : agendar.mutate())}
          >
            {pedeDivSlack ? <MessageSquare className="h-4 w-4" /> : <CalendarPlus className="h-4 w-4" />}
            {falta ??
              (pedeDivSlack
                ? 'Pedir aprovação a quem aprova DOH'
                : pedeAprovacao
                  ? 'Pedir aprovação no Slack'
                  : `Agendar para ${diaPorExtenso(data)}${janela && janelaOk ? `, ${janela.replace('-', ' às ')}` : ''}${divergencias.length ? ' com divergência' : ''}`)}
          </Botao>
        </div>
      </div>
    </Gaveta>
  );
}

const difTexto = (v: number): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${moeda(Math.abs(v))}`;

function Bloco({ titulo, icone, children }: { titulo: string; icone?: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <p className="rotulo flex items-center gap-1.5">
        {icone}
        {titulo}
      </p>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ data

export function isoDaqui(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function diaPorExtenso(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }).replace('.', '');
}

/** O campo de data do navegador (digita ou abre o calendário) + atalhos. */
export function CampoData({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }): JSX.Element {
  const atalhos: [string, string][] = [
    ['Hoje', isoDaqui(0)],
    ['Amanhã', isoDaqui(1)],
    ['Em 2 dias', isoDaqui(2)],
  ];
  return (
    <div className="space-y-2">
      <Campo type="date" value={valor} min={isoDaqui(0)} onChange={(e) => aoMudar(e.target.value)} />
      <div className="flex flex-wrap items-center gap-1.5">
        {atalhos.map(([rotulo, iso]) => (
          <button
            key={rotulo}
            type="button"
            onClick={() => aoMudar(iso)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs font-semibold transition',
              valor === iso ? 'border-primaria bg-primaria-suave text-primaria' : 'border-borda text-textoFraco hover:border-bordaForte',
            )}
          >
            {rotulo}
          </button>
        ))}
        {valor ? <span className="ml-auto text-xs capitalize text-textoSuave">{diaPorExtenso(valor)}</span> : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ janela

const JANELAS: [string, string][] = [
  ['Manhã', '08:00-12:00'],
  ['Tarde', '13:00-17:00'],
  ['Dia todo', '08:00-17:00'],
];

function janelaValida(v: string): boolean {
  const m = v.match(/^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/);
  return Boolean(m && `${m[1]}${m[2]}` < `${m[3]}${m[4]}`);
}

/** "0800 1200" digitado vira "08:00-12:00": a máscara força o padrão. */
function mascararJanela(bruto: string): string {
  const d = bruto.replace(/\D/g, '').slice(0, 8);
  let s = d.slice(0, 2);
  if (d.length > 2) s += `:${d.slice(2, 4)}`;
  if (d.length > 4) s += `-${d.slice(4, 6)}`;
  if (d.length > 6) s += `:${d.slice(6, 8)}`;
  return s;
}

function CampoJanela({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }): JSX.Element {
  const predefinida = JANELAS.some(([, v]) => v === valor);
  const [outra, definirOutra] = React.useState(!predefinida && valor !== '');
  const invalida = outra && valor.length === 11 && !janelaValida(valor);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {JANELAS.map(([rotulo, v]) => (
          <button
            key={v}
            type="button"
            onClick={() => {
              definirOutra(false);
              aoMudar(v);
            }}
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-left text-xs transition',
              !outra && valor === v ? 'border-primaria bg-primaria-suave text-primaria' : 'border-borda text-textoFraco hover:border-bordaForte',
            )}
          >
            <b className="block">{rotulo}</b>
            <span className="tabular">{v.replace('-', ' às ')}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            definirOutra(true);
            aoMudar('');
          }}
          className={cn(
            'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
            outra ? 'border-primaria bg-primaria-suave text-primaria' : 'border-borda text-textoFraco hover:border-bordaForte',
          )}
        >
          Outro horário
        </button>
      </div>
      {outra ? (
        <div className="space-y-1">
          <Campo
            autoFocus
            inputMode="numeric"
            placeholder="HH:MM-HH:MM  (ex.: 07:30-11:00)"
            value={valor}
            onChange={(e) => aoMudar(mascararJanela(e.target.value))}
            className={cn('tabular', invalida && 'border-status-divergencia')}
          />
          {invalida ? <p className="text-xs text-status-divergencia">Horário inválido: confira as horas e se o fim vem depois do começo.</p> : null}
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ transportadora

interface Transportadora {
  id: number;
  nome: string;
  cnpjRaiz: string | null;
  origem: string;
  usos: number;
}

const normalizar = (v: string): string =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

/**
 * Busca no cadastro (as que já trouxeram compra, mais usadas em cima). Nome
 * que não existe só entra depois de confirmar — é o que evita a mesma
 * transportadora escrita de três jeitos.
 */
function CampoTransportadora({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }): JSX.Element {
  const cliente = useQueryClient();
  const [texto, definirTexto] = React.useState(valor);
  const [aberta, definirAberta] = React.useState(false);
  const [confirmando, definirConfirmando] = React.useState(false);
  const [destaque, definirDestaque] = React.useState(0);
  const caixa = React.useRef<HTMLDivElement>(null);
  const lista = useQuery<{ transportadoras: Transportadora[] }>({
    queryKey: ['entradas-transportadoras'],
    queryFn: () => api.get('/api/entradas/transportadoras'),
    staleTime: 5 * 60_000,
  });
  const criar = useMutation({
    mutationFn: (nome: string) => api.post<{ id: number; nome: string; nova: boolean }>('/api/entradas/transportadoras', { nome }),
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: ['entradas-transportadoras'] });
      escolher(r.nome);
    },
  });

  React.useEffect(() => {
    const fora = (e: MouseEvent): void => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) definirAberta(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const termo = normalizar(texto);
  const digitos = texto.replace(/\D/g, '');
  const todas = lista.data?.transportadoras ?? [];
  const achadas = todas.filter((t) => !termo || normalizar(t.nome).includes(termo) || (digitos.length >= 3 && (t.cnpjRaiz ?? '').startsWith(digitos)));
  const exata = todas.find((t) => normalizar(t.nome) === termo);
  // "Cadastrar" só quando a busca não achou nada: com resultado na lista, o
  // certo é escolher um deles.
  const podeCriar = termo.length >= 3 && !exata && achadas.length === 0;

  function escolher(nome: string): void {
    definirTexto(nome);
    aoMudar(nome);
    definirAberta(false);
    definirConfirmando(false);
  }

  return (
    <div ref={caixa} className="relative space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
        <input
          className={cn(
            'h-10 w-full rounded-lg border bg-superficie pl-9 pr-9 text-sm shadow-xs placeholder:text-textoSuave',
            'focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12',
            valor && normalizar(valor) === termo ? 'border-status-livre' : 'border-borda',
          )}
          placeholder="Buscar transportadora pelo nome ou CNPJ"
          value={texto}
          onFocus={() => definirAberta(true)}
          onChange={(e) => {
            definirTexto(e.target.value);
            definirAberta(true);
            definirConfirmando(false);
            definirDestaque(0);
            // Só vale o que está no cadastro: digitar sem escolher não grava.
            aoMudar('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              definirDestaque((i) => Math.min(i + 1, achadas.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              definirDestaque((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const t = achadas[destaque];
              if (t) escolher(t.nome);
              else if (podeCriar) definirConfirmando(true);
            } else if (e.key === 'Escape') definirAberta(false);
          }}
        />
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
      </div>

      {aberta && !confirmando ? (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte">
          <ul className="max-h-60 overflow-y-auto py-1">
            {achadas.map((t, i) => (
              <li key={t.id}>
                <button
                  type="button"
                  onMouseEnter={() => definirDestaque(i)}
                  onClick={() => escolher(t.nome)}
                  className={cn('flex w-full items-center gap-3 px-3 py-2 text-left', i === destaque ? 'bg-superficie2' : '')}
                >
                  <Truck className="h-4 w-4 shrink-0 text-textoSuave" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">{t.nome}</span>
                    <span className="block text-xs text-textoSuave">
                      {t.cnpjRaiz ? `CNPJ ${t.cnpjRaiz.replace(/^(\d{2})(\d{3})(\d{3})$/, '$1.$2.$3')}/…` : 'cadastrada no WMS'}
                    </span>
                  </span>
                  {t.usos ? <span className="shrink-0 text-2xs font-semibold text-textoSuave">{t.usos} entrega(s)</span> : null}
                </button>
              </li>
            ))}
            {lista.isLoading ? <li className="px-3 py-2 text-xs text-textoSuave">Carregando o cadastro…</li> : null}
            {!lista.isLoading && !achadas.length && !podeCriar ? (
              <li className="px-3 py-2 text-xs text-textoSuave">Digite ao menos 3 letras.</li>
            ) : null}
          </ul>
          {podeCriar ? (
            <button
              type="button"
              onClick={() => definirConfirmando(true)}
              className="flex w-full items-center gap-2 border-t border-borda bg-superficie2 px-3 py-2 text-left text-[13px] font-semibold text-primaria hover:bg-primaria-suave"
            >
              <Plus className="h-4 w-4" /> Cadastrar “{texto.trim()}” como nova transportadora
            </button>
          ) : null}
        </div>
      ) : null}

      {confirmando ? (
        <div className="space-y-2 rounded-lg border border-status-atencao/40 bg-status-atencaoFundo px-3 py-2.5">
          <p className="text-[13px]">
            <b>“{normalizar(texto)}”</b> não está no cadastro. Confira se não é uma das que já existem (ex.: com outro nome ou sem “LTDA”). Cadastrar
            como nova?
          </p>
          {criar.error ? <p className="text-xs text-status-divergencia">{criar.error instanceof ErroHttp ? criar.error.message : String(criar.error)}</p> : null}
          <div className="flex justify-end gap-2">
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)}>
              Voltar
            </Botao>
            <Botao variante="primaria" tamanho="pequeno" carregando={criar.isPending} onClick={() => criar.mutate(texto)}>
              <Plus className="h-4 w-4" /> Cadastrar e usar
            </Botao>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ DOH

/**
 * O DOH numa frase por nota — "pode agendar" ou "trava: por quê" — e o
 * detalhe por item só para quem abrir. Antes: "NF …000615814: sem giro
 * (limite 120)", que ninguém entendia.
 */
const dataBr = (iso: string): string => iso.split('-').reverse().join('/');

/** De onde saiu o ritmo do item, quando não é a venda dos últimos 90 dias. */
function baseDoItem(x: Doh['itens'][number]): string | null {
  if (x.base === 'historico') return `sem venda há 90 dias · ritmo de ${dataBr(x.baseDe!)} a ${dataBr(x.baseAte!)}`;
  if (x.base === 'nunca_vendeu') return x.cadastro ? `nunca vendeu neste destino (cadastrado em ${dataBr(x.cadastro)})` : 'nunca vendeu neste destino';
  return null;
}

function QuadroDoh({ chaves, dohs }: { chaves: string[]; dohs: (Doh | undefined)[] }): JSX.Element {
  const carregando = dohs.some((d) => !d);
  const travadas = dohs.filter((d) => d?.travado).length;
  const acimaDoLimite = dohs.filter((d) => d?.acimaDoLimite).length;
  const semHistorico = dohs.filter((d) => d?.alerta).length;
  const limite = dohs.find((d) => d)?.limite;
  const escopo = dohs.find((d) => d?.escopo)?.escopo ?? null;
  return (
    <section className={cn('overflow-hidden rounded-xl border', travadas ? 'border-status-atencao/50' : 'border-borda')}>
      <div className={cn('flex items-start gap-3 px-4 py-3', travadas ? 'bg-status-atencaoFundo' : 'bg-status-livreFundo/50')}>
        {carregando ? (
          <span className="mt-0.5 h-5 w-5 animate-pulse rounded-full bg-superficie2" />
        ) : travadas ? (
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-status-atencao" />
        ) : (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-status-livre" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-bold">
            {carregando
              ? 'Calculando a cobertura de estoque…'
              : travadas
                ? [
                    acimaDoLimite ? `${acimaDoLimite} nota(s) passam do limite de ${limite} dias de estoque` : null,
                    semHistorico ? `${semHistorico} nota(s) com produto sem histórico de venda no destino` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : `Cobertura dentro do limite de ${limite} dias — pode agendar`}
          </p>
          <p className="text-xs text-textoFraco">
            {escopo
              ? `DOH em ${escopo.destino} = (estoque de hoje nos centros ${escopo.centros.join(', ')} + o que a nota traz) ÷ venda por dia que saiu do ${escopo.centroVenda}.`
              : 'DOH = quantos dias o estoque dura depois de receber: (estoque de hoje + o que a nota traz) ÷ venda por dia.'}
          </p>
          {!carregando && semHistorico ? (
            <p className="mt-1 text-xs font-semibold text-status-atencao">
              Sem venda neste destino não há como medir o DOH: libere com o motivo ou peça aprovação no Slack. Fica na linha do tempo da nota.
            </p>
          ) : null}
        </div>
      </div>
      <ul className="divide-y divide-borda">
        {chaves.map((c, i) => {
          const d = dohs[i];
          const nf = Number(c.slice(25, 34));
          if (!d) return <li key={c} className="h-11 animate-pulse bg-superficie2/60" />;
          const nunca = d.itens.filter((x) => x.base === 'nunca_vendeu');
          const pior = d.itens.filter((x) => x.dohFuturo != null).sort((a, b) => (b.dohFuturo ?? 0) - (a.dohFuturo ?? 0))[0];
          const resumo = [
            pior ? `pior item: ${Math.round(pior.dohFuturo ?? 0)} dias de estoque depois de receber${pior.base === 'historico' ? ' (pelo histórico)' : ''}` : null,
            nunca.length ? `${nunca.length} item(ns) que nunca venderam` : null,
          ].filter(Boolean);
          return (
            <li key={c}>
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-2.5 text-[13px] hover:bg-superficie2">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', d.travado ? 'bg-status-atencao' : d.alerta ? 'bg-status-atencao/50' : 'bg-status-livre')} />
                  <b className="tabular">NF {nf}</b>
                  <span className="min-w-0 flex-1 truncate text-textoFraco">
                    {resumo.length ? resumo.join(' · ') : 'sem itens para calcular'}
                  </span>
                  <span className={cn('shrink-0 text-xs font-semibold', d.travado || d.alerta ? 'text-status-atencao' : 'text-status-livre')}>
                    {d.travado ? 'trava' : d.alerta ? 'alerta' : 'ok'}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-textoSuave transition group-open:rotate-180" />
                </summary>
                <div className="overflow-x-auto bg-superficie2/50 px-4 pb-3">
                  <table className="w-full text-xs">
                    <thead className="text-left text-textoSuave">
                      <tr>
                        <th className="py-1.5 pr-2 font-semibold">Produto</th>
                        <th className="py-1.5 pr-2 text-right font-semibold">Na nota</th>
                        <th className="py-1.5 pr-2 text-right font-semibold">Estoque hoje</th>
                        <th className="py-1.5 pr-2 text-right font-semibold">Venda/dia</th>
                        <th className="py-1.5 pr-2 text-right font-semibold">DOH hoje</th>
                        <th className="py-1.5 text-right font-semibold">DOH depois</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-borda">
                      {d.itens.map((x) => (
                        <tr key={x.produto} className={cn((x.acima || x.base === 'nunca_vendeu') && 'text-status-atencao')}>
                          <td className="py-1.5 pr-2">
                            <span className="block max-w-[16rem] truncate font-medium text-texto">{x.descricao ?? `Produto ${x.produto}`}</span>
                            <span className="text-textoSuave">
                              {x.produto}
                              {baseDoItem(x) ? ` · ${baseDoItem(x)}` : ''}
                            </span>
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular">{x.quantidadeNota.toLocaleString('pt-BR')}</td>
                          <td className="py-1.5 pr-2 text-right tabular">{x.estoque.toLocaleString('pt-BR')}</td>
                          <td className="py-1.5 pr-2 text-right tabular">{x.vendaDia == null ? '—' : x.vendaDia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}</td>
                          <td className="py-1.5 pr-2 text-right tabular">{x.dohHoje == null ? '—' : `${Math.round(x.dohHoje)} dias`}</td>
                          <td className="py-1.5 text-right font-semibold tabular">{x.dohFuturo == null ? 'sem venda' : `${Math.round(x.dohFuturo)} dias`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
